import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { map, mergeMap, share, switchMap } from 'rxjs/operators';
import { ChannelsService } from '../../../api/gabby/channels/channels.service';
import { MessageModel } from '../../../api/gabby/messages/message.model';
import { MessagesService } from '../../../api/gabby/messages/messages.service';
import { WebsocketService } from '../../websocket.service';
import {
	GabbyChannelMessageUpdate,
	GabbyChannelMessagesRead,
	GabbyChannelNewMessage,
	GabbyChannelParticipantAdded,
	GabbyChannelParticipantRemoved,
	GabbyChannelRemoved,
	GabbyChannelUserTyping,
	GabbyNavigateRequestToUser,
	GabbyNewChannel,
} from './gabby-event-types';

@Injectable({
	providedIn: 'root',
})
export class GabbyWebsocketEventFactoryService {
	private _newMessage$?: Observable<GabbyChannelNewMessage>;
	private _newChannel$?: Observable<GabbyNewChannel>;

	constructor(
		private websocketService: WebsocketService,
		private channelsService: ChannelsService,
		private messagesService: MessagesService,
	) {}

	public newMessage$ = () => {
		if (this._newMessage$ == null) {
			this._newMessage$ = this.websocketService
				.subscribe$<
					[
						number,
						Pick<MessageModel, 'channel_id' | 'directory' | 'message_id'>,
					]
				>('gabby.channel.new_message')
				.pipe(
					mergeMap(([channelId, message]) =>
						this.messagesService
							.getMessage(
								message.directory,
								message.channel_id,
								message.message_id,
							)
							.pipe(
								map((fullMessage) => {
									return {
										channelId: channelId,
										message: fullMessage,
									} as GabbyChannelNewMessage;
								}),
							),
					),
					share(),
				);
		}

		return this._newMessage$;
	};

	public readMessages$ = () => {
		return this.websocketService
			.subscribe$<
				[string, number, number, number[], number]
			>('gabby.channel.messages.read')
			.pipe(
				map(
					([
						directoryId,
						channelId,
						readerLinkId,
						readMessageIds,
						senderLinkId,
					]) => {
						return {
							directoryId: directoryId,
							channelId: channelId,
							readerLinkId: readerLinkId,
							readMessageIds: readMessageIds,
							senderLinkId: senderLinkId,
						} as GabbyChannelMessagesRead;
					},
				),
			);
	};

	public updateMessage$ = () => {
		return this.websocketService
			.subscribe$<[number, MessageModel]>('gabby.channel.message.update')
			.pipe(
				map(([channelId, message]) => {
					return {
						channelId: channelId,
						message: plainToInstance(MessageModel, message),
					} as GabbyChannelMessageUpdate;
				}),
			);
	};

	public newChannel$ = () => {
		if (this._newChannel$ == null) {
			this._newChannel$ = this.websocketService
				.subscribe$<
					[
						string | number,
						{
							id: number;
							original_id: string | number;
							directory: string;
						},
					]
				>('gabby.new_channel')
				.pipe(
					mergeMap(([oldChannelId, channelRef]) =>
						this.channelsService
							.getChannel(channelRef.directory, channelRef.id)
							.pipe(
								map((channel) => {
									return {
										oldChannelId: oldChannelId,
										channel: channel,
									} as GabbyNewChannel;
								}),
							),
					),
					share(),
				);
		}

		return this._newChannel$;
	};

	public channelParticipantAdded$ = () => {
		return this.websocketService
			.subscribe$<[string, number, number[]]>('gabby.channel.participant.new')
			.pipe(
				map(([directoryId, channelId, addedUserLinkIds]) => {
					return {
						directoryId: directoryId,
						channelId: channelId,
						userLinkIds: addedUserLinkIds,
					} as GabbyChannelParticipantAdded;
				}),
			);
	};

	public channelParticipantRemoved$ = () => {
		return this.websocketService
			.subscribe$<
				[string, number, number[]]
			>('gabby.channel.participant.removed')
			.pipe(
				map(([directoryId, channelId, removedUserLinkIds]) => {
					return {
						directoryId: directoryId,
						channelId: channelId,
						userLinkIds: removedUserLinkIds,
					} as GabbyChannelParticipantRemoved;
				}),
			);
	};

	public channelRemoved$ = () => {
		return this.websocketService
			.subscribe$<[string, number]>('gabby.channel.removed')
			.pipe(
				map(([directoryId, channelId]) => {
					return {
						directoryId: directoryId,
						channelId: channelId,
					} as GabbyChannelRemoved;
				}),
			);
	};

	public channelFlagsChanged$ = () => {
		return this.websocketService.subscribe$<null>(
			'gabby.channel.flags.changed',
		);
	};

	public channelUserTyping$ = () => {
		return this.websocketService
			.subscribe$<[number, number, boolean]>('gabby.channel.user.typing')
			.pipe(
				map(([channelId, userLinkId, active]) => {
					return {
						channelId: channelId,
						userLinkId: userLinkId,
						active: active,
					} as GabbyChannelUserTyping;
				}),
			);
	};

	public navigateRequestToUser$ = () => {
		return this.websocketService
			.subscribe$<[number]>('gabby.navigate.request.user')
			.pipe(
				map(([userLinkId]) => {
					return {
						userLinkId: userLinkId,
					} as GabbyNavigateRequestToUser;
				}),
				switchMap((r) =>
					this.websocketService
						.publish$('gabby.navigate.request.user.ack', [r.userLinkId])
						.pipe(map(() => r)),
				),
			);
	};
}
